import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Award, Users, Shield, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_META = {
  "network-marketing": { title: "Network Marketing in mare aperto", icon: Users },
  leadership: { title: "Leadership digitale", icon: BookOpen },
  "tutela-digitale": { title: "Tutela nel digitale", icon: Shield },
};

export default function MemberReviews() {
  const [data, setData] = useState({ items: [], counts: { total: 0, avg: 0 } });

  useEffect(() => {
    (async () => {
      try {
        const slugs = Object.keys(COURSE_META);
        const results = await Promise.all(
          slugs.map((s) => axios.get(`${API}/courses/${s}/reviews?limit=8`).then((r) => ({ slug: s, ...r.data })).catch(() => null))
        );
        const items = [];
        let totalCount = 0;
        let weightedSum = 0;
        for (const r of results) {
          if (!r) continue;
          totalCount += r.count;
          weightedSum += r.average * r.count;
          for (const rv of (r.items || [])) {
            if ((rv.comment || "").trim().length > 20) {
              items.push({ ...rv, slug: r.slug });
            }
          }
        }
        items.sort((a, b) => (b.rating - a.rating) || ((b.created_at || "") > (a.created_at || "") ? 1 : -1));
        setData({
          items: items.slice(0, 6),
          counts: { total: totalCount, avg: totalCount ? +(weightedSum / totalCount).toFixed(1) : 0 },
        });
      } catch {
        /* silent */
      }
    })();
  }, []);

  if (!data.items.length) return null;

  return (
    <section
      data-testid="member-reviews-section"
      className="py-20 md:py-28 bg-[#F4E8D6]/30"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
            <Award size={14} className="text-[#E89B6C]" />
            <span className="overline text-[#E89B6C]">Recensioni dei membri</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-[#0B192C]">
            Voci di chi sta <em className="text-[#E89B6C]">navigando</em> con noi.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={20} fill={n <= Math.round(data.counts.avg) ? "#E89B6C" : "transparent"} stroke="#E89B6C" />
              ))}
            </div>
            <span className="font-serif-display text-xl text-[#0B192C]">{data.counts.avg.toFixed(1)}/5</span>
            <span className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">· {data.counts.total} recensioni</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
          {data.items.map((r, i) => {
            const meta = COURSE_META[r.slug] || {};
            const Icon = meta.icon || BookOpen;
            return (
              <article
                key={i}
                data-testid={`member-review-${i}`}
                className="bg-white p-6 md:p-8 flex flex-col gap-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={14} fill={n <= r.rating ? "#E89B6C" : "transparent"} stroke="#E89B6C" />
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
                    <Icon size={10} /> {meta.title || r.slug}
                  </span>
                </div>
                <p className="text-base font-light leading-relaxed text-[#0B192C] italic flex-1">
                  &ldquo;{r.comment}&rdquo;
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B] pt-3 border-t border-[#E5E1DA]">
                  — {r.member_name}
                </p>
              </article>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/area-iscritti"
            data-testid="cta-area-iscritti"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C] link-underline"
          >
            Accedi all&apos;Area Iscritti per lasciare la tua recensione <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </section>
  );
}
